import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FlaskConical, Plus, Search, Loader2, Zap } from "lucide-react";

const STATUS_LABELS = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  in_progress: { label: "In progress", cls: "bg-sky-100 text-sky-800 border-sky-300" },
  post_fill: { label: "Post-fill", cls: "bg-orange-100 text-orange-800 border-orange-300" },
  completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  archived: { label: "Archived", cls: "bg-slate-100 text-slate-500 border-slate-300" },
};

export function StatusBadge({ status, className = "" }) {
  const s = STATUS_LABELS[status] || STATUS_LABELS.draft;
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${s.cls} ${className}`}>{s.label}</span>;
}

export default function BatchLogs() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [creating, setCreating] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get("/batch-logs", { params: { q, status } });
      setRows(data.batches || []);
      setCounts(data.counts || {});
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load batch logs"); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [status]);
  useEffect(() => { const t = setTimeout(reload, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  const chips = useMemo(() => [
    { v: "", label: "Active" },
    { v: "draft", label: `Draft (${counts.draft || 0})` },
    { v: "in_progress", label: `In progress (${counts.in_progress || 0})` },
    { v: "post_fill", label: `Post-fill (${counts.post_fill || 0})` },
    { v: "completed", label: `Completed (${counts.completed || 0})` },
    { v: "archived", label: `Archived (${counts.archived || 0})` },
  ], [counts]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <FlaskConical className="w-7 h-7 text-pink-700" /> Batch Log
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Every batch spawns from a Master Formula, auto-scales ingredients, and walks Draft → In Progress → Post-fill → Completed with digital sign-off.
          </p>
        </div>
        {isAdmin && (
          <Button data-testid="batchlogs-new-btn" onClick={() => setCreating(true)}
            className="bg-pink-700 hover:bg-pink-800 gap-1.5">
            <Plus className="w-4 h-4" /> New Batch
          </Button>
        )}
      </div>

      <Card className="p-3 mb-4 border-slate-200 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="batchlogs-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search batch #, formula, lot…" className="pl-9 h-9 text-sm" />
        </div>
        <div className="flex flex-wrap gap-1">
          {chips.map((c) => (
            <button key={c.v || "active"} onClick={() => setStatus(c.v)}
              data-testid={`batchlogs-chip-${c.v || "active"}`}
              className={`text-xs px-3 py-1 rounded-full border font-medium ${
                status === c.v ? "bg-pink-700 text-white border-pink-700" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}>{c.label}</button>
          ))}
        </div>
      </Card>

      <Card className="p-0 overflow-hidden border-slate-200">
        {rows.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">
            No batch logs {status ? `in ${status.replace("_", " ")}` : "yet"}. {isAdmin && !status && "Click New Batch to spawn one from a Master Formula."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="batchlogs-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Batch #</TableHead>
                  <TableHead>Formula</TableHead>
                  <TableHead>Lot #</TableHead>
                  <TableHead className="text-right">Batch size</TableHead>
                  <TableHead>Planned start</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Compounded by</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((b) => (
                  <TableRow key={b.id} data-testid={`batchlog-row-${b.id}`}>
                    <TableCell>
                      <Link to={`/batch-logs/${b.id}`} data-testid={`batchlog-open-${b.id}`}
                        className="font-mono text-xs text-pink-800 hover:text-pink-900 hover:underline">
                        {b.batch_number}
                      </Link>
                    </TableCell>
                    <TableCell className="text-sm text-slate-800">{b.formula_name}</TableCell>
                    <TableCell className="font-mono text-xs">{b.lot_number || <span className="text-slate-300">—</span>}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-slate-600">
                      {b.total_quantity} {b.total_quantity_unit}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">{b.planned_start}</TableCell>
                    <TableCell><StatusBadge status={b.status} /></TableCell>
                    <TableCell className="text-xs text-slate-500">{(b.compounded_by || {}).name || (b.compounded_by || {}).email || <span className="text-slate-300">—</span>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <NewBatchDialog open={creating} onClose={() => setCreating(false)}
        onCreated={(id) => { setCreating(false); window.location.href = `/batch-logs/${id}`; }} />
    </div>
  );
}

function NewBatchDialog({ open, onClose, onCreated }) {
  const [formulas, setFormulas] = useState([]);
  const [formulaId, setFormulaId] = useState("");
  const [batchAmount, setBatchAmount] = useState("");
  const [plannedStart, setPlannedStart] = useState(new Date().toISOString().slice(0, 10));
  const [autoFill, setAutoFill] = useState(true);
  const [printAfter, setPrintAfter] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/formulas").then((r) => setFormulas(r.data.formulas || [])).catch(() => {});
    setFormulaId(""); setBatchAmount("");
    setPlannedStart(new Date().toISOString().slice(0, 10));
    setAutoFill(true); setPrintAfter(false);
  }, [open]);

  const selected = formulas.find((f) => f.id === formulaId);
  // Default the batch amount to the formula's base yield when a formula is picked
  useEffect(() => {
    if (selected && !batchAmount) {
      setBatchAmount(String(selected.total_quantity || ""));
    }
    // eslint-disable-next-line
  }, [formulaId]);

  const create = async () => {
    if (!formulaId) return toast.error("Pick a Master Formula");
    const amt = Number(batchAmount);
    if (!amt || amt <= 0) return toast.error("Enter a batch amount");
    setSaving(true);
    try {
      const { data } = await api.post("/batch-logs", {
        formula_id: formulaId,
        batch_amount: amt,
        planned_start: plannedStart,
        auto_fill: autoFill,
      });
      toast.success(`Batch ${data.batch_number} created · lot ${data.lot_number}${autoFill ? " · lots auto-filled" : ""}`);
      if (printAfter) {
        // Fire the browser download of the worksheet immediately so the tech can print
        const token = localStorage.getItem("access_token");
        window.open(
          `${process.env.REACT_APP_BACKEND_URL}/api/batch-logs/${data.id}/pdf?auth=${encodeURIComponent(token || "")}`,
          "_blank",
        );
      }
      onCreated(data.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  const unit = selected?.total_quantity_unit || "";
  const base = selected?.total_quantity || 0;
  const scaleFactor = base > 0 && Number(batchAmount) > 0 ? Number(batchAmount) / base : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Spawn Batch from Master Formula</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] text-slate-500 uppercase">Master formula *</label>
            <select data-testid="batch-formula-select" value={formulaId}
              onChange={(e) => setFormulaId(e.target.value)}
              className="w-full h-9 text-sm px-2 rounded-md border border-slate-200">
              <option value="">— pick a recipe —</option>
              {formulas.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name} {f.total_quantity ? `(base ${f.total_quantity} ${f.total_quantity_unit})` : ""}
                </option>
              ))}
            </select>
          </div>
          {selected && (
            <div className="text-[11px] text-slate-500 bg-slate-50 rounded p-2 border border-slate-200 space-y-0.5">
              <div><b>{(selected.ingredients || []).length}</b> ingredients · BUD <b>{selected.bud_days || 0}</b> days ({selected.bud_rule || "—"})</div>
              <div>Storage: {selected.storage_conditions || "—"}</div>
              <div>Recipe base yield: <b>{base} {unit}</b></div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-slate-500 uppercase">
                Batch amount {unit ? `(${unit})` : ""} *
              </label>
              <Input data-testid="batch-amount" type="number" step="any" min="0.01" value={batchAmount}
                onChange={(e) => setBatchAmount(e.target.value)}
                placeholder={base ? `e.g. ${base}` : "100"}
                className="h-9 text-sm font-mono" />
              {selected && scaleFactor > 0 && Math.abs(scaleFactor - 1) > 0.0001 && (
                <p className="text-[11px] text-slate-500 mt-1">
                  Ingredients scale × {scaleFactor.toFixed(3)}
                </p>
              )}
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase">Planned start</label>
              <Input data-testid="batch-planned-start" type="date" value={plannedStart}
                onChange={(e) => setPlannedStart(e.target.value)} className="h-9 text-sm" />
            </div>
          </div>

          {/* Fast-track toggles */}
          <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 space-y-2">
            <div className="text-[10px] uppercase text-emerald-800 font-semibold flex items-center gap-1.5">
              <Zap className="w-3 h-3" /> Fast-track
            </div>
            <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
              <input type="checkbox" data-testid="batch-autofill" checked={autoFill}
                onChange={(e) => setAutoFill(e.target.checked)}
                className="w-4 h-4 accent-emerald-700 mt-0.5" />
              <span>
                <b>Auto-fill lots & NDCs from previous batch.</b> Ingredients, vials, stoppers,
                caps and disinfection agents pull their inventory link + lot from the most recent
                batch of this recipe. Falls back to FEFO from stock if no history. Tech can still
                edit any row before printing.
              </span>
            </label>
            <label className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer">
              <input type="checkbox" data-testid="batch-print-after" checked={printAfter}
                onChange={(e) => setPrintAfter(e.target.checked)}
                className="w-4 h-4 accent-emerald-700 mt-0.5" />
              <span>
                <b>Open printable worksheet after create.</b> Skips digital data entry and jumps
                straight to the PDF for hand-written completion.
              </span>
            </label>
          </div>

          <div className="text-[11px] text-slate-500 bg-amber-50 border border-amber-200 rounded p-2">
            Lot number is auto-generated (next 4-digit sequential). Editable on the batch detail if needed.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button data-testid="batch-create-save" onClick={create} disabled={saving}
            className="bg-pink-700 hover:bg-pink-800 gap-1.5">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" />
                    : (autoFill && printAfter ? <Zap className="w-4 h-4" /> : <Plus className="w-4 h-4" />)}
            {autoFill && printAfter ? "Fast-track + print" : "Spawn batch"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
